import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { fetchData } from '../api/publicService';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PlaceIcon from '@mui/icons-material/Place';

import CtaBackgroundImage from '../assets/images/best1.png';
import Rating from '../components/Rating';

const PALETTE = {
  stoneWhite: '#F1F1F1',
  peacockTeal: '#005B5C',
  saffronOrange: '#FCA510',
  textPrimary: '#1A2027',
  textSecondary: '#5F5F5F',
};

const colors = {
  primary: '#005F69',
  primaryAccent: '#F28C28',
  secondaryAccent: '#B9935A',
  text: '#7B3F00',
  background: '#F1F1F1',
};

const JourneyCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.07)',
  transition: 'transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    '& .destination-overlay': {
      transform: 'translateY(0)',
      opacity: 1,
    },
  },
}));

const DestinationOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '220px',
  padding: theme.spacing(2),
  color: 'white',
  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.2) 100%)',
  backdropFilter: 'blur(3px)',
  transform: 'translateY(100%)',
  opacity: 0,
  transition: 'transform 0.4s ease-in-out, opacity 0.4s ease-in-out',
  overflowY: 'auto',
}));

const getCurrencySymbol = (currencyCode) => {
  switch (currencyCode.toLowerCase()) {
    case 'eur': return '€';
    case 'gbp': return '£';
    case 'usd': default: return '$';
  }
};

const Trips = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { language } = useLanguage();
  const { currency } = useCurrency();
  
  // --- NEW: State to control how many trips are visible ---
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const loadTrips = async () => {
      try {
        setLoading(true);
        const data = await fetchData('/trips', language);
        // Assuming the backend sends the latest trips first. 
        // If not, you could sort them here:
        // const sortedData = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setTrips(data);
        setError(null);
      } catch (err) {
        setError('Could not fetch tours. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    loadTrips();
  }, [language]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>;
  if (error) return <Container sx={{ py: 8 }}><Alert severity="error">{error}</Alert></Container>;

  const handleShowMore = () => {
    setVisibleCount(trips.length); // Show all trips
  };

  return (
    <Box component="section" sx={{ backgroundColor: PALETTE.stoneWhite }}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography variant="h4" align="center" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: colors.text, mb: 2 }}>
            Related Journeys
          </Typography>
            <Divider sx={{ my: 3, mx: 'auto', width: '80px', borderWidth: '1px', borderColor: colors.secondaryAccent }} />
        </Box>

        <Grid container spacing={4}>
          {/* --- UPDATED: Use .slice() to show only a limited number of trips --- */}
          {trips.slice(0, visibleCount).map((trip) => {
            const uniqueDestinations = [...new Set(
              trip.itinerary
                .map(day => day.destination?.name)
                .filter(Boolean)
            )];
            
            const price = trip.basePrice?.[currency] || trip.basePrice?.usd || 0;
            const symbol = getCurrencySymbol(currency);

            return (
              // --- CODE QUALITY FIX: Correct MUI Grid props ---
              <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={trip._id}>
                <JourneyCard>
                  <Box sx={{ position: 'relative', height: '220px' }}>
                    <CardMedia
                      component="img"
                      height="220"
                      image={trip.images[0]}
                      alt={trip.title}
                    />
                    <DestinationOverlay className="destination-overlay">
                      <Typography variant="h6" fontWeight="bold" gutterBottom>Key Destinations</Typography>
                      <List dense disablePadding>
                        {uniqueDestinations.slice(0, 4).map((name, index) => (
                          <ListItem key={index} disablePadding>
                            <ListItemIcon sx={{ minWidth: '32px' }}><PlaceIcon sx={{ color: 'white', fontSize: '1rem' }} /></ListItemIcon>
                            <ListItemText primary={name} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                          </ListItem>
                        ))}
                      </List>
                    </DestinationOverlay>
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      {trip.duration} Days • {trip.tripType?.name}
                    </Typography>
                    <Typography gutterBottom variant="h5" component="h3" sx={{ fontWeight: 600, color: '#005F69', mt: 1, mb: 2 }}>
                      {trip.title}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 2, color: PALETTE.saffronOrange }}>
                      From {symbol}{price.toLocaleString()} / person
                    </Typography>
                    <Typography variant="body2" color={PALETTE.textSecondary} sx={{
                      display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {trip.summary}
                    </Typography>
                    <Rating value={trip.totalRatings} text={`(${trip.ratings.length})`} />
                  </CardContent>
                  <CardActions sx={{ px: 3, pb: 3, mt: 'auto' }}>
                    <Button
                      component={Link}
                      to={`/trips/${trip.slug}`}
                      variant="contained"
                      fullWidth
                      size="large"
                      sx={{
                        backgroundColor: PALETTE.peacockTeal,
                        fontWeight: 600,
                        py: 1.5,
                        '&:hover': { backgroundColor: '#004243' },
                      }}
                    >
                      View Itinerary
                    </Button>
                  </CardActions>
                </JourneyCard>
              </Grid>
            );
          })}
        </Grid>

        {/* --- NEW: "Show More" Button --- */}
        {/* This button only appears if there are more trips to show */}
        {trips.length > visibleCount && (
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={handleShowMore}
              sx={{
                color: PALETTE.peacockTeal,
                borderColor: PALETTE.peacockTeal,
                fontWeight: 600,
                px: 5,
                py: 1.5,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 91, 92, 0.08)',
                  borderColor: PALETTE.peacockTeal,
                }
              }}
            >
              Show All Journeys
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Trips;